import { useRef, useState } from "react";
import {
    Loader2,
    Download as DownloadIcon,
    Upload,
    Database,
    AlertTriangle,
    CheckCircle2,
    FileArchive,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function ToolsManager() {
    const [exporting, setExporting] = useState(false);
    const [importing, setImporting] = useState(false);
    const [exportingZip, setExportingZip] = useState(false);
    const [importingZip, setImportingZip] = useState(false);
    const [wipe, setWipe] = useState(false);
    const [wipeZip, setWipeZip] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [summary, setSummary] = useState(null);
    const fileRef = useRef(null);
    const zipFileRef = useRef(null);

    const doExport = async () => {
        setExporting(true);
        setError("");
        setSuccess("");
        try {
            const { data } = await api.get("/admin/export");
            const blob = new Blob([JSON.stringify(data, null, 2)], {
                type: "application/json",
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            const stamp = new Date()
                .toISOString()
                .replace(/[:T]/g, "-")
                .slice(0, 16);
            a.href = url;
            a.download = `projectrs-backup-${stamp}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            setSuccess("Eksport zakończony - plik zapisany w pobranych.");
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setExporting(false);
        }
    };

    const doImport = async () => {
        const f = fileRef.current?.files?.[0];
        if (!f) {
            setError("Wybierz plik .json wyeksportowany wcześniej z tego panelu.");
            return;
        }
        const confirmMsg = wipe
            ? "Wybrałeś TRYB ZASTĄPIENIA - istniejące dane (galeria, archiwum, dodatki, zespół, FAQ, POI, social, ustawienia) zostaną najpierw usunięte, a następnie zaimportowane z pliku. Tej operacji nie da się cofnąć. Kontynuować?"
            : "Wykonać import (tryb scalenia z istniejącymi danymi)?";
        if (!window.confirm(confirmMsg)) return;
        setImporting(true);
        setError("");
        setSuccess("");
        setSummary(null);
        try {
            const text = await f.text();
            const json = JSON.parse(text);
            const { data } = await api.post("/admin/import", {
                data: json,
                wipe,
            });
            setSummary(data.summary || {});
            setSuccess("Import zakończony.");
            if (fileRef.current) fileRef.current.value = "";
        } catch (e) {
            if (e?.name === "SyntaxError") {
                setError("Plik nie jest poprawnym JSON-em.");
            } else {
                setError(
                    formatApiErrorDetail(e.response?.data?.detail) || e.message,
                );
            }
        } finally {
            setImporting(false);
        }
    };

    const doExportZip = async () => {
        setExportingZip(true);
        setError("");
        setSuccess("");
        try {
            const res = await api.get("/admin/export-full", {
                responseType: "blob",
            });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            const stamp = new Date()
                .toISOString()
                .replace(/[:T]/g, "-")
                .slice(0, 16);
            a.href = url;
            a.download = `projectrs-backup-full-${stamp}.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            setSuccess(
                `Pełny eksport ZIP zakończony (${(res.data.size / 1024 / 1024).toFixed(2)} MB) - plik zapisany w pobranych.`,
            );
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setExportingZip(false);
        }
    };

    const doImportZip = async () => {
        const f = zipFileRef.current?.files?.[0];
        if (!f) {
            setError("Wybierz plik .zip wyeksportowany wcześniej z tego panelu.");
            return;
        }
        const confirmMsg = wipeZip
            ? "TRYB ZASTĄPIENIA dla pełnej kopii - usuniemy WSZYSTKO (kolekcje + pliki GridFS + statystyki + zgłoszenia) zanim wczytamy archiwum. Operacji NIE da się cofnąć. Kontynuować?"
            : "Wczytać pełną kopię (scalenie z istniejącymi danymi)?";
        if (!window.confirm(confirmMsg)) return;
        setImportingZip(true);
        setError("");
        setSuccess("");
        setSummary(null);
        try {
            const form = new FormData();
            form.append("file", f);
            form.append("wipe", wipeZip ? "true" : "false");
            const { data } = await api.post("/admin/import-full", form, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 600_000,
            });
            setSummary(data.summary || {});
            setSuccess("Pełny import ZIP zakończony.");
            if (zipFileRef.current) zipFileRef.current.value = "";
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setImportingZip(false);
        }
    };

    return (
        <div data-testid="admin-tools" className="grid lg:grid-cols-2 gap-6">
            {/* Export */}
            <div className="panel-yellow p-6">
                <div className="label-eyebrow flex items-center gap-2 mb-3">
                    <Database size={14} /> Kopia zapasowa
                </div>
                <p className="text-sm text-[color:var(--text-secondary)] mb-4">
                    Wyeksportuj wszystkie dane treści (galeria, archiwum, dodatki,
                    zespół, FAQ, POI, kategorie POI, social, ustawienia moda) do
                    jednego pliku JSON. <strong className="text-white">Pliki
                    binarne</strong> (obrazy, plik moda) i statystyki nie są
                    zawarte w eksporcie.
                </p>
                <button
                    data-testid="tools-export-btn"
                    onClick={doExport}
                    disabled={exporting}
                    className="btn-primary w-full disabled:opacity-60"
                >
                    {exporting ? (
                        <Loader2 className="animate-spin" size={14} />
                    ) : (
                        <DownloadIcon size={14} />
                    )}
                    Pobierz kopię JSON
                </button>
            </div>

            {/* Import */}
            <div className="panel-yellow p-6">
                <div className="label-eyebrow flex items-center gap-2 mb-3">
                    <Upload size={14} /> Przywróć z kopii
                </div>
                <p className="text-sm text-[color:var(--text-secondary)] mb-4">
                    Wczytaj wcześniej pobrany plik JSON. Domyślnie dane są{" "}
                    <strong className="text-white">scalane</strong> z istniejącymi
                    (upsert po polu <code>id</code>). Tryb zastąpienia usuwa
                    najpierw obecne dane.
                </p>
                <input
                    type="file"
                    accept="application/json,.json"
                    ref={fileRef}
                    data-testid="tools-import-file"
                    className="text-sm text-[color:var(--text-secondary)] mb-3 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold"
                />
                <label className="flex items-center gap-2 text-sm text-[color:var(--text-secondary)] cursor-pointer mb-4 select-none">
                    <input
                        type="checkbox"
                        data-testid="tools-wipe-checkbox"
                        checked={wipe}
                        onChange={(e) => setWipe(e.target.checked)}
                        className="accent-[color:var(--primary)]"
                    />
                    <AlertTriangle
                        size={14}
                        className="text-[color:var(--primary)]"
                    />
                    <span>
                        Tryb zastąpienia - wyczyść kolekcje przed importem
                        (nieodwracalne)
                    </span>
                </label>
                <button
                    data-testid="tools-import-btn"
                    onClick={doImport}
                    disabled={importing}
                    className="btn-primary w-full disabled:opacity-60"
                >
                    {importing ? (
                        <Loader2 className="animate-spin" size={14} />
                    ) : (
                        <Upload size={14} />
                    )}
                    Importuj dane
                </button>
            </div>

            {/* Section separator */}
            <div className="lg:col-span-2 flex items-center gap-3 mt-2">
                <div className="h-px flex-1 bg-white/10" />
                <span className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)] flex items-center gap-2">
                    <FileArchive size={12} /> Pełna kopia (ZIP) - z plikami i statystykami
                </span>
                <div className="h-px flex-1 bg-white/10" />
            </div>

            {/* ZIP Export */}
            <div className="panel-yellow p-6">
                <div className="label-eyebrow flex items-center gap-2 mb-3">
                    <FileArchive size={14} /> Eksport pełny (ZIP)
                </div>
                <p className="text-sm text-[color:var(--text-secondary)] mb-4">
                    Pobierz <strong className="text-white">wszystko</strong> w jednym
                    pliku ZIP: dane treści + statystyki + zgłoszenia błędów +{" "}
                    <strong className="text-white">obrazy</strong> i{" "}
                    <strong className="text-white">plik moda</strong> z GridFS.
                    URL-e plików są zachowane - po przywróceniu nic nie pęknie.
                </p>
                <button
                    data-testid="tools-export-zip-btn"
                    onClick={doExportZip}
                    disabled={exportingZip}
                    className="btn-primary w-full disabled:opacity-60"
                >
                    {exportingZip ? (
                        <Loader2 className="animate-spin" size={14} />
                    ) : (
                        <DownloadIcon size={14} />
                    )}
                    Pobierz pełną kopię (ZIP)
                </button>
                {exportingZip && (
                    <div className="text-[10px] text-[color:var(--text-muted)] mt-2 text-center">
                        Pakowanie plików może chwilę potrwać przy dużej galerii…
                    </div>
                )}
            </div>

            {/* ZIP Import */}
            <div className="panel-yellow p-6">
                <div className="label-eyebrow flex items-center gap-2 mb-3">
                    <Upload size={14} /> Przywróć pełną kopię (ZIP)
                </div>
                <p className="text-sm text-[color:var(--text-secondary)] mb-4">
                    Wgraj wcześniej pobrany ZIP. Zdjęcia i plik moda trafią z
                    powrotem do GridFS z oryginalnymi identyfikatorami - wszystkie
                    linki w treści pozostaną aktywne.
                </p>
                <input
                    type="file"
                    accept=".zip,application/zip,application/x-zip-compressed"
                    ref={zipFileRef}
                    data-testid="tools-import-zip-file"
                    className="text-sm text-[color:var(--text-secondary)] mb-3 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold"
                />
                <label className="flex items-center gap-2 text-sm text-[color:var(--text-secondary)] cursor-pointer mb-4 select-none">
                    <input
                        type="checkbox"
                        data-testid="tools-wipe-zip-checkbox"
                        checked={wipeZip}
                        onChange={(e) => setWipeZip(e.target.checked)}
                        className="accent-[color:var(--primary)]"
                    />
                    <AlertTriangle
                        size={14}
                        className="text-[color:var(--primary)]"
                    />
                    <span>
                        Tryb zastąpienia - wyczyść kolekcje{" "}
                        <strong className="text-white">i pliki GridFS</strong>{" "}
                        przed importem (nieodwracalne)
                    </span>
                </label>
                <button
                    data-testid="tools-import-zip-btn"
                    onClick={doImportZip}
                    disabled={importingZip}
                    className="btn-primary w-full disabled:opacity-60"
                >
                    {importingZip ? (
                        <Loader2 className="animate-spin" size={14} />
                    ) : (
                        <Upload size={14} />
                    )}
                    Importuj pełną kopię (ZIP)
                </button>
                {importingZip && (
                    <div className="text-[10px] text-[color:var(--text-muted)] mt-2 text-center">
                        Wgrywanie zawartości do GridFS - proszę nie zamykać strony…
                    </div>
                )}
            </div>

            {/* Status row */}
            {(error || success || summary) && (
                <div className="lg:col-span-2">
                    {error && (
                        <div
                            data-testid="tools-error"
                            className="panel-yellow p-4 text-sm text-red-400 flex items-center gap-2"
                        >
                            <AlertTriangle size={16} /> {error}
                        </div>
                    )}
                    {success && (
                        <div
                            data-testid="tools-success"
                            className="panel-yellow p-4 text-sm text-[color:var(--primary)] flex items-center gap-2"
                        >
                            <CheckCircle2 size={16} /> {success}
                        </div>
                    )}
                    {summary && (
                        <div
                            data-testid="tools-summary"
                            className="panel-yellow p-4 mt-3 overflow-x-auto"
                        >
                            <div className="label-eyebrow mb-3">
                                Podsumowanie importu
                            </div>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-muted)] text-left">
                                        <th className="py-1.5">Kolekcja</th>
                                        <th className="py-1.5">Dodano</th>
                                        <th className="py-1.5">Zaktualizowano</th>
                                        <th className="py-1.5">Tryb</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(summary).map(([name, s]) => (
                                        <tr
                                            key={name}
                                            className="border-t border-white/5"
                                        >
                                            <td className="py-1.5 text-white">
                                                {name}
                                            </td>
                                            <td className="py-1.5 text-[color:var(--primary)]">
                                                {s.inserted}
                                            </td>
                                            <td className="py-1.5 text-[color:var(--text-secondary)]">
                                                {s.updated}
                                            </td>
                                            <td className="py-1.5 text-[color:var(--text-muted)]">
                                                {s.wiped
                                                    ? "zastąpienie"
                                                    : "scalenie"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
